import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, resolveImageUrl } from "@/lib/api";
import { fmtMoney, fmtKm } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { VehicleFormDialog } from "@/components/VehicleFormDialog";

const statusColors = {
  available: "border-green-500/40 text-green-600",
  pending: "border-amber-500/40 text-amber-600",
  sold: "border-zinc-300 text-zinc-500",
  draft: "border-blue-500/40 text-blue-600",
  archived: "border-zinc-300 text-zinc-500",
};

export default function AdminInventory() {
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [v, c] = await Promise.all([api.get("/admin/vehicles"), api.get("/admin/categories")]);
      setVehicles(v.data);
      setCategories(c.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const catTitle = (slug) => categories.find((c) => c.slug === slug)?.title || slug;

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/admin/vehicles/${deleting.id}`);
      toast.success("Vehicle deleted");
      setDeleting(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div data-testid="admin-inventory">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl font-black uppercase text-zinc-900">Inventory</h1>
          <p className="text-sm text-zinc-500" data-testid="admin-inventory-count">{vehicles.length} vehicles in system</p>
        </div>
        <Button
          onClick={() => { setEditing(null); setFormOpen(true); }}
          className="bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm"
          data-testid="add-vehicle-button"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Vehicle
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div>
      ) : (
        <div className="bg-white border border-zinc-200 overflow-x-auto scrollbar-dark">
          <table className="w-full text-sm" data-testid="admin-vehicles-table">
            <thead>
              <tr className="border-b border-zinc-200 text-left">
                {["Vehicle", "Category", "Price", "Mileage", "Status", "Featured", "Actions"].map((h) => (
                  <th key={h} className="px-5 py-4 text-xs font-mono uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vehicles.map((v) => (
                <tr key={v.id} className="border-b border-zinc-200 hover:bg-zinc-100" data-testid={`admin-vehicle-row-${v.id}`}>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3 min-w-[220px]">
                      <div className="w-16 h-11 bg-zinc-100 rounded-sm overflow-hidden shrink-0">
                        {v.images?.[0] && <img src={resolveImageUrl(v.images[0])} alt="" className="w-full h-full object-cover" loading="lazy" />}
                      </div>
                      <div>
                        <p className="font-bold text-zinc-900 leading-tight">{v.title}</p>
                        <p className="text-xs text-zinc-500 font-mono">
                          {v.year} {v.make} {v.model}{v.trim ? ` ${v.trim}` : ""}{v.stock_number ? ` · #${v.stock_number}` : ""}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-zinc-600 whitespace-nowrap">{catTitle(v.category)}</td>
                  <td className="px-5 py-3.5 whitespace-nowrap">
                    <span className="text-[#FF6B00] font-bold">{fmtMoney(v.sale_price || v.price)}</span>
                    {v.sale_price && v.sale_price < v.price && (
                      <span className="text-xs text-zinc-500 line-through ml-2">{fmtMoney(v.price)}</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-zinc-600 whitespace-nowrap">{fmtKm(v.mileage)}</td>
                  <td className="px-5 py-3.5">
                    <Badge variant="outline" className={`uppercase text-[10px] tracking-wider rounded-sm ${statusColors[v.status] || ""}`}>
                      {v.status}
                    </Badge>
                  </td>
                  <td className="px-5 py-3.5 text-zinc-600">{v.featured ? "Yes" : "—"}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditing(v); setFormOpen(true); }}
                        className="p-2 border border-zinc-300 rounded-sm text-zinc-500 hover:text-[#FF6B00] hover:border-[#FF6B00] transition-colors"
                        aria-label={`Edit ${v.title}`}
                        data-testid={`edit-vehicle-${v.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => setDeleting(v)}
                        className="p-2 border border-zinc-300 rounded-sm text-zinc-500 hover:text-red-600 hover:border-red-500 transition-colors"
                        aria-label={`Delete ${v.title}`}
                        data-testid={`delete-vehicle-${v.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {vehicles.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-14 text-center text-zinc-500" data-testid="admin-vehicles-empty">
                    No vehicles yet. Add your first unit.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <VehicleFormDialog open={formOpen} onOpenChange={setFormOpen} vehicle={editing} categories={categories} onSaved={fetchAll} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-white border-zinc-200 text-zinc-900" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase text-zinc-900">Delete Vehicle?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-500">
              "{deleting?.title}" will be permanently removed from inventory. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-300 text-zinc-600 rounded-sm" data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteBusy}
              className="bg-red-600 hover:bg-red-500 text-white rounded-sm font-bold uppercase text-xs tracking-wider"
              data-testid="delete-confirm-button"
            >
              {deleteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
